<template>
    <template v-if="isAdminRoute">
        <div class="h-screen bg-gray-50 flex overflow-hidden root">
            <Menu v-if="currentRouteName !== 'login'" :is_menu_open="isMenuOpen" @close="handleCloseMenu"/>

            <main class="flex-1 overflow-auto focus:outline-none" tabindex="0">
                <Header
                    v-if="currentRouteName !== 'login'"
                    @openMenu="handleOpenMenu"
                    @logout="handleLogout"
                />
                <RouterView />
                <ModalProvider />
                <ToastProvider />
            </main>
        </div>
    </template>
    <template v-else>
        <Loader v-if="isPending"/>
        <div v-else class="root">
            <RouterView />
            <ModalProvider />
            <ToastProvider />
        </div>
    </template>
</template>

<script>
    import ModalProvider from "./providers/modals";
    import ToastProvider from "./providers/toasts";

    import Menu from "./components/partials/menu";
    import Header from "./components/partials/header";
    import Loader from "./components/partials/loader";

    export default {
        beforeMount() {
            this.$store.dispatch('frontoffice/getDomain', { includes: [
                'settings','files','videos','videos.formats','videos.thumb','tours','tours.thumb',
                'projects', 'projects.settings', 'projects.buildings', 'projects.domain',
                'projects.buildings.buildings', 'projects.buildings.settings', 'projects.buildings.floors',
                'projects.buildings.floors.units', 'pages',
                'projects.buildings.floors.units.status', 'projects.buildings.floors.units.blueprints2D', 'projects.buildings.floors.units.unit', 'projects.buildings.floors.units.unit.status','projects.buildings.floors.units.unit.blueprints2D', 'projects.buildings.floors.units.category', 'projects.buildings.floors.units.units'
            ]}).then( () => {
                if(this.domain && this.domain.settings && this.domain.settings.data) {
                    let domainCSSFileUrl = this.domain.settings.data.find( setting => setting.name === 'custom-css' ) && this.domain.settings.data.find( setting => setting.name === 'custom-css' ).css_file ? this.domain.settings.data.find( setting => setting.name === 'custom-css' ).css_file.url : null

                    if(domainCSSFileUrl && this.$route.query['custom-css'] === '1') {
                        let headTag = document.querySelector('head');
                        let linkTag = document.createElement('link');
                        linkTag.setAttribute('href', domainCSSFileUrl);
                        linkTag.setAttribute('rel', 'stylesheet');
                        linkTag.setAttribute('type', 'text/css');

                        headTag.append(linkTag);
                    }
                }
            });
        },
        data() {
            return {
                isMenuOpen: false,
            }
        },
        computed: {
            domain() {
                return this.$store.state.frontoffice.domain;
            },
            isPending() {
                return this.$store.state.frontoffice.isPending;
            },
            currentRouteName() {
                return this.$route.name;
            },
            isAdminRoute() {
                return this.$route.path.split('/').filter( item => item )[0] === 'admin'
            },
            settings() {
                return this.authUser ? this.authUser.corporation.settings.data : [];
            },
            authUser() {
                return this.$store.state.auth.auth_user;
            },
            mainColor() {
                let mainColor = this.settings.find( setting => setting.name === 'main-color' ) && this.settings.find( setting => setting.name === 'main-color' ).value ? this.settings.find( setting => setting.name === 'main-color' ).value : '#ffffff'

                return {
                    '800': this.getLightenDarkenColor(mainColor, -100),
                    '600': this.getLightenDarkenColor(mainColor, -50),
                    '300': this.getLightenDarkenColor(mainColor, 50),
                    '100': this.getLightenDarkenColor(mainColor, 100)
                }
            },
            secondaryColor() {
                let secondaryColor = this.settings.find( setting => setting.name === 'secondary-color' ) && this.settings.find( setting => setting.name === 'secondary-color' ).value ? this.settings.find( setting => setting.name === 'secondary-color' ).value : '#000000'

                return {
                    '800': this.getLightenDarkenColor(secondaryColor, -100),
                    '600': this.getLightenDarkenColor(secondaryColor, -50),
                    '300': this.getLightenDarkenColor(secondaryColor, 50),
                    '100': this.getLightenDarkenColor(secondaryColor, 100)
                }
            },
            domainMainColor() {
                if(this.domain && this.domain.settings && this.domain.settings.data) {
                    return this.domain.settings.data.find( setting => setting.name === 'main-color' ) && this.domain.settings.data.find( setting => setting.name === 'main-color' ).value ? this.domain.settings.data.find( setting => setting.name === 'main-color' ).value : '#000000'
                }

                return "#ff0000";
            },
        },
        methods: {
            handleOpenMenu() {
                this.isMenuOpen = true;
            },
            handleCloseMenu() {
                this.isMenuOpen = false;
            },
            handleLogout() {
                this.$store.dispatch('auth/logout')
            },
            getLightenDarkenColor(color, amount) {
                return '#' + color.replace(/^#/, '').replace(/../g, color => ('0'+Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
            }
        },
        components: {
            ModalProvider,
            ToastProvider,
            Menu,
            Header,
            Loader
        }
    }
</script>

<style>
    .root{
        --main-color-800: v-bind(mainColor[800]);
        --main-color-600: v-bind(mainColor[600]);
        --main-color-300: v-bind(mainColor[300]);
        --main-color-100: v-bind(mainColor[800]);
        --secondary-color-800: v-bind(secondaryColor[800]);
        --secondary-color-600: v-bind(secondaryColor[600]);
        --secondary-color-300: v-bind(secondaryColor[300]);
        --secondary-color-100: v-bind(secondaryColor[100]);
        --base-color: v-bind(domainMainColor);
    }
    .bg-custom-800 {
        background-color: var(--main-color-800)
    }
    .hover\:bg-custom-800:hover {
        background-color: var(--main-color-800)
    }
    .bg-custom-600 {
        background-color: var(--main-color-600)
    }
    .hover\:bg-custom-600:hover {
        background-color: var(--main-color-600)
    }
    .bg-custom-100 {
        background-color: var(--main-color-100)
    }
    .hover\:bg-custom-100:hover {
        background-color: var(--main-color-100)
    }
    .text-custom-100 {
        color: var(--secondary-color-100)
    }
    .hover\:text-custom-100:hover {
        color: var(--secondary-color-100)
    }
    .text-custom-300 {
        color: var(--secondary-color-300)
    }
    .hover\:text-custom-300:hover {
        color: var(--secondary-color-300)
    }
</style>
